import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Trash2, Edit, User } from 'lucide-react';
import api from '../services/api';
import type { Notification } from '../types/notification';
import Modal from '../components/common/Modal';
import { Input } from '../components/common/Input';
import { Textarea } from '../components/common/Textarea';
import { Select } from '../components/common/Select';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface NotificationFormData {
  type: string;
  title: string;
  message: string;
  targetUsers: string;
  targetRoles: string;
  targetDepartments: string;
  priority: string;
}

const SystemNotifications: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedNotification, setSelectedNotification] = React.useState<Notification | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<NotificationFormData>();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['system-notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications/all');
      return response.data.notifications || [];
    }
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data.users || [];
    }
  });

  const createNotificationMutation = useMutation({
    mutationFn: async (data: NotificationFormData) => {
      const response = await api.post('/notifications/broadcast', {
        type: data.type,
        title: data.title,
        message: data.message,
        targetUsers: data.targetUsers ? data.targetUsers.split(',').map(id => parseInt(id.trim())) : [],
        targetRoles: data.targetRoles ? data.targetRoles.split(',').map(id => parseInt(id.trim())) : [],
        targetDepartments: data.targetDepartments ? data.targetDepartments.split(',').map(id => parseInt(id.trim())) : [],
        priority: data.priority || 'normal'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-notifications'] });
      toast.success('Notification sent successfully');
      setIsModalOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error sending notification');
    }
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/notifications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-notifications'] });
      toast.success('Notification deleted');
      setDeleteModalOpen(false);
      setSelectedNotification(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error deleting notification');
    }
  });

  const onSubmit = (data: NotificationFormData) => {
    createNotificationMutation.mutate(data);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'system':
        return 'blue';
      case 'reminder':
        return 'purple';
      case 'announcement':
        return 'green';
      default:
        return 'gray';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">System Notifications</h2>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Send Notification
        </Button>
      </div>

      {isLoading ? (
        <div>Loading notifications...</div>
      ) : (
        <div className="space-y-4">
          {notifications?.map((notification: Notification) => (
            <div
              key={notification.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-start"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Badge color={getTypeColor(notification.type)}>
                    {notification.type}
                  </Badge>
                  {notification.priority && (
                    <Badge color={getPriorityColor(notification.priority)}>
                      {notification.priority}
                    </Badge>
                  )}
                  <span className="text-sm text-gray-500">
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
                <h3 className="font-medium">{notification.title || 'Notification'}</h3>
                <p className="text-gray-600 mt-1">{notification.message}</p>
                {notification.user && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                    <User className="h-4 w-4" />
                    <span>Sent by: {notification.user.first_name} {notification.user.last_name}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedNotification(notification);
                    setDeleteModalOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {(!notifications || notifications.length === 0) && (
            <div className="text-center py-12 text-gray-500">
              No system notifications yet
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          reset();
        }}
        title="Send System Notification"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Select
            label="Type"
            {...register('type', { required: 'Type is required' })}
            error={errors.type?.message}
          >
            <option value="">Select type</option>
            <option value="system">System</option>
            <option value="reminder">Reminder</option>
            <option value="announcement">Announcement</option>
            <option value="alert">Alert</option>
          </Select>

          <Select
            label="Priority"
            {...register('priority')}
          >
            <option value="normal">Normal</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>

          <Input
            label="Title"
            {...register('title', { required: 'Title is required' })}
            error={errors.title?.message}
          />

          <Textarea
            label="Message"
            {...register('message', { required: 'Message is required' })}
            error={errors.message?.message}
            rows={4}
          />

          <Input
            label="Target User IDs"
            {...register('targetUsers')}
            placeholder="Comma-separated user IDs (optional)"
          />

          <Input
            label="Target Role IDs"
            {...register('targetRoles')}
            placeholder="Comma-separated role IDs (optional)"
          />

          <Input
            label="Target Department IDs"
            {...register('targetDepartments')}
            placeholder="Comma-separated department IDs (optional)"
          />

          <div className="text-sm text-gray-600">
            Leave targeting fields empty to send to all users.
          </div>

          <div className="flex gap-3 pt-4">
            <Button 
              type="submit" 
              disabled={createNotificationMutation.isPending}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              Send Notification
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedNotification(null);
        }}
        title="Delete Notification"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete this notification?</p>
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={() => {
                if (selectedNotification) {
                  deleteNotificationMutation.mutate(selectedNotification.id);
                }
              }}
              disabled={deleteNotificationMutation.isPending}
            >
              Delete
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedNotification(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SystemNotifications;